import { Cpu, HardDrive, MemoryStick, Server as ServerIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fromFirstReading } from '@/components/charts/series';
import { Sparkline } from '@/components/charts/Sparkline';
import { IconTile, Pill, SectionCard, StatusPill } from '@/components/panels/SectionCard';
import { formatUptime } from '@/lib/metrics-api';
import { relativeTime } from '@/modules/monitoring/format';
import type { FleetServer } from '@/types/api';

/**
 * Every server the customer owns, side by side.
 *
 * ## There is deliberately no fleet-wide CPU figure
 *
 * Averaging CPU across different machines describes none of them: a four-core box at 100% and a
 * thirty-two-core box idle average to something comfortable while one of them is on fire. Each
 * number here belongs to one server.
 *
 * The only fleet-level number is a **count** — how many are reporting — which is meaningful
 * because it counts servers rather than averaging unrelated quantities.
 *
 * ## One server gets a different card, not a stretched one
 *
 * A lonely tile in a three-column grid looks like a bug, so a single server spans the full width.
 * But spanning it with the same three numbers only moved the problem: a card 1,400px wide holding
 * "0.2%  8.1%  9.9%" and a great deal of nothing reads as unfinished, not as spacious.
 *
 * So the wide card has a different body. Gauges on the left, and the width on the right carries the
 * CPU and memory trends side by side, each captioned with the period it actually covers. The extra
 * space is filled with information that was already in the payload rather than with air.
 */
export function ServerFleet({
  servers,
  reporting,
  loading,
  error = null,
}: {
  servers: FleetServer[];
  reporting: number;
  loading: boolean;
  /** Why the figures could not be read. Without it a failed request drew "No servers yet". */
  error?: string | null;
}) {
  if (loading) {
    return (
      <SectionCard title="Your servers" subtitle="Live resource utilisation">
        <p className="text-sm text-muted-foreground">Reading from your servers…</p>
      </SectionCard>
    );
  }

  if (error !== null) {
    return (
      <SectionCard title="Your servers" subtitle="Live resource utilisation">
        <p className="text-sm text-danger">The latest readings could not be loaded: {error}</p>
      </SectionCard>
    );
  }

  if (servers.length === 0) {
    return (
      <SectionCard title="Your servers" subtitle="Live resource utilisation">
        <p className="text-sm text-muted-foreground">
          No servers yet.{' '}
          <Link to="/servers" className="text-primary hover:underline">
            Add one
          </Link>{' '}
          and the platform will start sampling its CPU, memory and disk every five minutes.
        </p>
      </SectionCard>
    );
  }

  const single = servers.length === 1;

  // Columns follow the count as well as the width. Two servers in a three-column grid left a
  // third of the panel empty, and on a wide screen a fleet of four or more gets a fourth column
  // rather than cards stretched to twice the width their contents need.
  const columns = single
    ? ''
    : servers.length === 2
      ? 'md:grid-cols-2'
      : servers.length === 3
        ? 'md:grid-cols-2 xl:grid-cols-3'
        : 'md:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4';

  return (
    <SectionCard
      title="Your servers"
      subtitle="Live resource utilisation, sampled every five minutes"
      action={
        <Pill tone={reporting === servers.length ? 'default' : 'warning'}>
          {reporting} of {servers.length} reporting
        </Pill>
      }
    >
      <div className={`grid grid-cols-1 gap-4 ${columns}`}>
        {servers.map((server) => (
          <ServerCard key={server.id} server={server} wide={single} />
        ))}
      </div>
    </SectionCard>
  );
}

function ServerCard({ server, wide }: { server: FleetServer; wide: boolean }) {
  const reported = server.sampled_at !== null;

  // Stale means the collector has not managed a reading in over three intervals — the same rule
  // monitoring uses. A card showing 4% CPU from two hours ago is worse than one admitting it does
  // not know.
  const stale =
    reported && Date.now() - new Date(server.sampled_at ?? '').getTime() > 3 * 300_000;

  return (
    <Link
      to={`/servers/${server.id}`}
      className="block rounded-xl border p-4 transition-colors hover:border-primary/40 hover:bg-muted/30"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <IconTile icon={ServerIcon} colour="text-blue-400" />
          <div className="min-w-0">
            <p className="truncate font-semibold leading-tight">{server.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {server.ip_address ?? server.endpoint}
              {server.os !== '' && ` · ${server.os}`}
            </p>
          </div>
        </div>

        <StatusPill
          label={!reported ? 'No data' : stale ? 'Stale' : server.ready ? 'Online' : 'Unverified'}
          tone={!reported ? 'muted' : stale ? 'warning' : server.ready ? 'success' : 'warning'}
        />
      </div>

      {!reported ? (
        <p className="text-sm text-muted-foreground">
          Waiting for its first reading. CPU and network need a second sample before they can be
          worked out.
        </p>
      ) : (
        <>
          {/* One server gets a two-column body rather than one stretched column.
              A single card spanning the full dashboard with three numbers in it is emptier than
              the lonely tile it replaced — the width has to be earned by content, not padding.
              So the space to the right carries the second trend line, drawn from `memory_series`,
              which the payload has always included and nothing was showing. */}
          <div className={wide ? 'grid items-start gap-6 lg:grid-cols-2' : ''}>
            <div className="grid grid-cols-3 gap-3">
              <Gauge icon={Cpu} colour="text-blue-400" label="CPU" value={server.cpu_percent} />
              <Gauge
                icon={MemoryStick}
                colour="text-purple-400"
                label="Memory"
                value={server.memory_percent}
              />
              <Gauge
                icon={HardDrive}
                colour="text-amber-400"
                label="Disk"
                value={server.disk_percent}
              />
            </div>

            {wide ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Trend label="CPU" values={server.cpu_series} colour="rgb(96 165 250)" />
                <Trend label="Memory" values={server.memory_series} colour="rgb(192 132 252)" />
              </div>
            ) : (
              // Captioned here too, not only in the wide card. Two servers side by side draw
              // their charts at the same width over whatever history each of them has, so one
              // added this morning and one added ten minutes ago produce identically sized
              // charts covering wildly different periods. Unlabelled, that is not a chart.
              <div className="mt-3">
                <Trend
                  label="CPU"
                  values={server.cpu_series}
                  colour="rgb(96 165 250)"
                  height={36}
                />
              </div>
            )}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {server.uptime_seconds !== null && server.uptime_seconds > 0 && `up ${formatUptime(server.uptime_seconds)} · `}
            load/core {server.load_per_core ?? 'not reported'} · read {relativeTime(server.sampled_at ?? '')}
          </p>
        </>
      )}
    </Link>
  );
}

/**
 * A labelled trend line, with the window it covers stated.
 *
 * The window is derived from how many readings came back rather than written as a constant. A
 * server connected an hour ago has twelve samples, not the full set, and a chart captioned "last
 * 24 hours" over twelve points would be describing a period it cannot see.
 */
function Trend({
  label,
  values,
  colour,
  height = 48,
}: {
  label: string;
  values: (number | null)[];
  colour: string;
  height?: number;
}) {
  // Counted from the first real reading, the same trim the chart draws from. Counting the raw
  // array would caption a ten-minute-old server "last 2 hours" because the window asked for two
  // hours — the window is what was requested, not what came back.
  const covered = fromFirstReading(values).length;

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-[11px] text-muted-foreground/70">{coveredBy(covered)}</span>
      </div>
      <Sparkline values={values} color={colour} max={100} height={height} />
    </div>
  );
}

/** Readings are taken every five minutes; this turns a count of them into a period. */
function coveredBy(points: number): string {
  if (points < 2) {
    return 'first reading';
  }

  // The span between readings, not the number of them: three readings cover ten minutes, not
  // fifteen.
  const minutes = (points - 1) * 5;

  if (minutes < 60) {
    return `last ${minutes} min`;
  }

  const hours = Math.round(minutes / 60);

  return `last ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

function Gauge({
  icon,
  colour,
  label,
  value,
}: {
  icon: typeof Cpu;
  colour: string;
  label: string;
  value: number | null;
}) {
  // Amber past 75%, red past 90%. Disk is the one that actually stops installs and backups working, and
  // by the time somebody notices it is usually already full.
  const tone =
    value === null ? '' : value >= 90 ? 'text-danger' : value >= 75 ? 'text-amber-500' : '';

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <IconTile icon={icon} colour={colour} size="sm" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className={`text-lg font-semibold tabular-nums ${tone}`}>
        {value === null ? <span className="text-xs font-normal text-muted-foreground">Not reported</span> : `${value.toFixed(1)}%`}
      </p>
    </div>
  );
}
